import React from 'react';
import { useTranslation } from 'react-i18next';
import NoticeDetail from './NoticeDetail';
import HomeLatestPreview from './home/HomeLatestPreview';
import HomeRankingsPreview from './home/HomeRankingsPreview';
import HomeGlassCard from './home/HomeGlassCard';
import HeroWaves from './home/HeroWaves';
import { theme, buttonClass } from '../ui/theme';
import { useUser } from '../UserContext';
import { AdminContext } from '../AdminContext';

export default function HomeLanding({ currentLang, onNavigate }) {
  const { t } = useTranslation();
  const [noticeId, setNoticeId] = React.useState(null);
  const { user } = useUser();
  const { isAdmin } = React.useContext(AdminContext);
  const isLoggedIn = !!user || !!isAdmin;
  const memberCtaLabel = isLoggedIn
    ? t('navigation.memberCenter', '会员中心')
    : t('auth.loginOrRegister', '会员服务');
  const memberCtaPath = isLoggedIn ? '/profile' : '/login';

  const go = (path) => {
    const langPrefix = currentLang ? `/${currentLang}` : '';
    onNavigate(`${langPrefix}${path}`);
  };

  const goMusicTab = (tab) => {
    const q = tab ? `?tab=${encodeURIComponent(String(tab))}` : '';
    go(`/music${q}`);
  };

  const goCategoryHash = (hash) => {
    const h = String(hash || '').replace(/^#/, '');
    go(`/music${h ? `#${h}` : ''}`);
  };
  const btnPrimary = buttonClass({ variant: 'primary', size: 'md' });
  const btnOutline = buttonClass({ variant: 'outline', size: 'md' });
  // 首页：灰色按钮统一改为与“开始聆听”一致的蓝色风格
  const btnNeutral = btnOutline;
  // "你可以在这里做什么"板块使用小尺寸按钮
  const btnNeutralSmall = buttonClass({ variant: 'outline', size: 'sm' });

  return (
    <main className="pt-0 pb-4 space-y-2">
      {/* A. Hero：诗性产品首屏 */}
      <section className="px-0">
        <div>
          <HomeGlassCard className="p-5 relative overflow-hidden">
            {/* 叠加动效：不改变 Hero 原背景色（只叠加透明 canvas） */}
            <HeroWaves className="opacity-70" density={1 / 22000000} color={0xfbbf24} />

            <div className="relative z-10 text-center">
              <div className="text-xs text-white/60 mb-2">{t('home.hero')}</div>
              <h1 className="text-2xl md:text-3xl font-semibold text-white">{t('appTitle')}</h1>
              {/* 副标题：单独一行放在标题下方 */}
              <p className="mt-1 text-base text-white/90">{t('appSubtitle')}</p>
              {/* 徽章移动到副标题左侧（移动端自动换行） */}
              <div className="mt-2 flex flex-col items-center gap-2 md:flex-row md:justify-center md:gap-3">
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                  {[
                    t('homeLanding.chipMultiLang', '多语言分类'),
                    t('homeLanding.chipFavorites', '收藏'),
                    t('homeLanding.chipOffline', '离线缓存'),
                    t('homeLanding.chipPlaylists', '歌单')
                  ].map((x) => (
                    <span key={x} className="text-xs text-white/70 inline-flex items-center gap-1.5 cursor-default select-none">
                      <img
                        src="/icons/7star.png"
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        className="w-3.5 h-3.5 opacity-80"
                      />
                      {x}
                    </span>
                  ))}
                </div>
              </div>
              <p className="mt-3 text-lg text-white/65 max-w-2xl mx-auto">
                {t(
                  'homeLanding.heroDesc',
                  t('homeLanding.subtitle', '在一个页面里听歌、收藏、整理歌单，并用多语言更轻松地找到你想听的作品。')
                )}
              </p>

              {/* Hero CTA：仅保留两个按钮，居中 */}
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {/* 默认活动态：音乐库 */}
                <button className={btnPrimary} onClick={() => goMusicTab('library')}>
                  {t('homeLanding.heroBtnLibrary', '音乐库')}
                </button>
                <button className={btnOutline} onClick={() => go(memberCtaPath)}>
                  {memberCtaLabel}
                </button>
                {/* 普通态：创作团队 */}
                <button className={btnNeutral} onClick={() => go('/about')}>
                  {t('homeLanding.heroBtnTenryu', '创作团队')}
                </button>
              </div>
            </div>
          </HomeGlassCard>
        </div>
      </section>

      {/* B. 探索入口：五类分类 */}
      <section className="px-0 lg:px-1">
        <div className="max-w-6xl mx-auto px-0 lg:px-3">
          <HomeGlassCard>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-white text-lg font-semibold">
                  {t('homeLanding.exploreTitle', '从分类开始探索')}
                </h2>
                <div className="text-xs text-white/60 mt-1">
                  {t('homeLanding.exploreDesc', '选择语言/主题，马上进入音乐库。')}
                </div>
              </div>
              <button
                type="button"
                className={theme.links.subtleUnderline}
                onClick={() => goMusicTab('library')}
              >
                {t('homeLanding.exploreAll', '浏览全部')}
              </button>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {[
                { key: 'zh', title: t('nav.music_zh', '中文歌'), icon: '/icons/panda.png', desc: t('homeLanding.exploreZhDesc', '中文旋律，讲述每一种情绪') },
                { key: 'ja', title: t('nav.music_ja', '日文歌'), icon: '/icons/Cherry%20blossoms.png', desc: t('homeLanding.exploreJaDesc', '旋律与词，像风一样。') },
                { key: 'en', title: t('nav.music_en', '英文歌'), icon: '/icons/Beethoven.png', desc: t('homeLanding.exploreEnDesc', '把世界装进一句副歌。') },
                { key: 'world', title: t('nav.music_world', '世界歌曲'), icon: '/icons/Pigeon.png', desc: t('homeLanding.exploreWorldDesc', '来自不同地方的声音。') },
                { key: 'pure', title: t('nav.music_pure', '纯音乐'), icon: '/icons/music.png', desc: t('homeLanding.explorePureDesc', '没有语言，也能抵达。') }
              ].map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => goCategoryHash(c.key)}
                  className={[
                    'text-left rounded-md transition-colors px-4 py-3',
                    theme.buttons.variant.outline,
                    // 分类入口是“卡片按钮”，这里不走默认按钮尺寸/排版
                    'inline-block w-full'
                  ].join(' ')}
                >
                  <div className="flex items-center gap-3">
                    {c.icon ? (
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                        <img
                          src={c.icon}
                          alt=""
                          aria-hidden="true"
                          draggable={false}
                          className="w-full h-full object-contain opacity-90"
                        />
                      </div>
                    ) : null}
                    <div className="min-w-0">
                      <div className="text-white font-semibold">{c.title}</div>
                      <div className="mt-1 text-xs text-white/60 leading-relaxed">{c.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </HomeGlassCard>
        </div>
      </section>

      {/* C + D. 预览：排行榜 / 最近更新 */}
      <section className="px-0 lg:px-1">
        <div className="max-w-6xl mx-auto px-0 lg:px-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
          <HomeRankingsPreview
            currentLang={currentLang}
            onOpenAll={() => goMusicTab('rankings')}
          />
          <HomeLatestPreview
            currentLang={currentLang}
            onOpenAll={() => goMusicTab('library')}
          />
        </div>
      </section>

      {/* E. 你可以在这里做什么 */}
      <section className="px-0 lg:px-1">
        <div className="max-w-6xl mx-auto px-0 lg:px-3">
          <HomeGlassCard>
            <h2 className="text-white text-lg font-semibold mb-3">
              {t('homeLanding.capabilitiesTitle', '你可以在这里做什么')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="border border-white/10 bg-white/[0.03] rounded-md p-4 flex flex-col">
                <div className="text-white font-semibold">
                  {t('homeLanding.feature1.title', '多语言音乐专区')}
                </div>
                <div className="mt-1 text-sm text-white/65">
                  {t('homeLanding.feature1.desc', '中日英...多种世界音乐，想听就听。')}
                </div>
                <div className="mt-auto pt-3">
                  <button className={btnNeutralSmall} onClick={() => goCategoryHash('zh')}>
                    {t('homeLanding.featureCtaCategory', '分类浏览')}
                  </button>
                </div>
              </div>

              <div className="border border-white/10 bg-white/[0.03] rounded-md p-4 flex flex-col">
                <div className="text-white font-semibold">
                  {t('homeLanding.feature2.title', '把喜欢的歌留住')}
                </div>
                <div className="mt-1 text-sm text-white/65">
                  {t('homeLanding.feature2.desc', '收藏、建歌单、随时回放，让好音乐不再错过。')}
                </div>
                <div className="mt-auto pt-3 flex flex-wrap gap-2">
                  <button className={btnNeutralSmall} onClick={() => goMusicTab('favorites')}>
                    {t('homeLanding.featureCtaFavorites', '我的收藏')}
                  </button>
                  <button className={btnNeutralSmall} onClick={() => goMusicTab('playlists')}>
                    {t('homeLanding.featureCtaPlaylists', '管理歌单')}
                  </button>
                </div>
              </div>

              <div className="border border-white/10 bg-white/[0.03] rounded-md p-4 flex flex-col">
                <div className="text-white font-semibold">
                  {t('homeLanding.feature3.title', '把时间留给音乐')}
                </div>
                <div className="mt-1 text-sm text-white/65">
                  {t('homeLanding.feature3.desc', '多条件筛选，一键直达最爱。')}
                </div>
                <div className="mt-auto pt-3">
                  <button className={btnNeutralSmall} onClick={() => goMusicTab('search')}>
                    {t('homeLanding.featureCtaSearch', '高级搜索')}
                  </button>
                </div>
              </div>

              <div className="border border-white/10 bg-white/[0.03] rounded-md p-4 flex flex-col">
                <div className="text-white font-semibold">
                  {t('homeLanding.feature4.title', '离线缓存，随时可听')}
                </div>
                <div className="mt-1 text-sm text-white/65">
                  {t('homeLanding.feature4.desc', '把喜欢的作品缓存到本地，没有网络也能播放。')}
                </div>
                <div className="mt-auto pt-3">
                  <button className={btnNeutralSmall} onClick={() => go('/offline')}>
                    {t('homeLanding.featureCtaOffline', '缓存歌曲')}
                  </button>
                </div>
              </div>
            </div>
          </HomeGlassCard>
        </div>
      </section>

      {/* F. 最新公告（延迟挂载，避免首屏额外请求）- 已隐藏 */}
      {/* <section className="px-0 lg:px-1">
        <div className="max-w-6xl mx-auto px-0 lg:px-3">
          <LazyMount>
            <div className="rounded-md">
              <NoticeBar onNoticeClick={(id) => setNoticeId(id)} />
            </div>
          </LazyMount>
        </div>
      </section> */}

      {/* G. 品牌落款（折叠式《我是生命》）- 已隐藏 */}
      {/* <section className="px-0 lg:px-1">
        <div className="max-w-6xl mx-auto px-0 lg:px-3">
          <HomeGlassCard>
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-white text-lg font-semibold">{t('home.feature.title')}</h2>
              <button
                type="button"
                className={theme.links.subtleUnderline}
                onClick={() => setManifestoOpen((v) => !v)}
              >
                {manifestoOpen
                  ? t('homeLanding.manifestoLess', '收起')
                  : t('homeLanding.manifestoMore', '展开全文')}
              </button>
            </div>
            <div className="mt-2 relative">
              <pre
                className={[
                  'whitespace-pre-wrap text-sm text-white/70 leading-relaxed',
                  manifestoOpen ? '' : 'max-h-40 overflow-hidden'
                ].join(' ')}
              >
                {t('home.feature.body')}
              </pre>
              {!manifestoOpen && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0b1220] to-transparent" />
              )}
            </div>
          </HomeGlassCard>
        </div>
      </section> */}

      {/* H. 结尾 CTA */}
      <section className="px-0 lg:px-1">
        <div className="max-w-6xl mx-auto px-0 lg:px-3">
          <HomeGlassCard>
            <h2 className="text-white text-lg font-semibold mb-1">{t('homeLanding.footerCtaTitle', '准备好开始聆听了吗？')}</h2>
            <div className="text-sm text-white/65">{t('homeLanding.footerCtaDesc', '从一首歌开始，慢慢把喜欢的声音收进自己的歌单。')}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button className={btnOutline} onClick={() => goMusicTab('library')}>
                {t('homeLanding.footerCtaPlay', '开始聆听')}
              </button>
              <button className={btnPrimary} onClick={() => go('/login')}>
                {t('auth.loginOrRegister', '会员服务')}
              </button>
              <button className={btnNeutral} onClick={() => go('/about')}>
                {t('navigation.aboutSite', '本站介绍')}
              </button>
            </div>
          </HomeGlassCard>
        </div>
      </section>

      {/* 公告详情弹窗 */}
      {noticeId ? <NoticeDetail noticeId={noticeId} onClose={() => setNoticeId(null)} /> : null}
    </main>
  );
}

